import { leadsService } from "@/app/services/leadsService";
import { LeadDetails } from "@/app/services/leadsService/getLeadId";
import masks from "@/app/utils/masks";
import { Button } from "@/view/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/view/components/ui/dialog";
import { Spinner } from "@/view/components/ui/spinner";
import { useToast } from "@/view/components/ui/use-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface DetailsLeadModalProps {
  visible: boolean;
  onClose: () => void;
  leadId: string | null;
  onContact: (lead: LeadDetails) => void;
}

const DetailsLeadModal = ({
  visible,
  onClose,
  leadId,
  onContact,
}: DetailsLeadModalProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: lead, isLoading } = useQuery({
    queryKey: ["lead-details", leadId],
    queryFn: () => leadsService.getLeadId(leadId!),
    enabled: !!leadId && visible,
  });

  const isContacted = !!lead?.contacted;

  const formatTime = (dateString: string) =>
    new Date(dateString).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });

  const formatDayLabel = (dateString: string) => {
    const date = new Date(dateString + "T00:00:00");
    const today = new Date();

    const isToday = date.toDateString() === today.toDateString();

    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isToday) return "Hoje";
    if (isYesterday) return "Ontem";

    return date.toLocaleDateString("pt-BR");
  };

  const handleContact = () => {
    if (!lead) return;

    onContact(lead);
    onClose();
  };

  const deleteLeadMutation = useMutation({
    mutationFn: (id: string) => leadsService.deleteLead(id),
    onSuccess: () => {
      toast({
        variant: "default",
        title: "Lead deletado com sucesso",
      });

      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["linkbyId"] });

      onClose();
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: error?.message || "Erro ao deletar lead",
      });
    },
  });

  const handleDelete = () => {
    if (!leadId) return;

    deleteLeadMutation.mutate(leadId);
  };

  const handleSimulation = () => {
    navigate("/transacoes", {
      state: { openChooseSimulation: true },
    });

    onClose();
  };

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Detalhes do lead</DialogTitle>
          <DialogDescription>
            Abaixo você pode visualizar e realizar ações referente ao lead
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex w-full items-center justify-center py-10">
            <Spinner />
          </div>
        ) : (
          <div className="items-center">
            <div className="flex w-full flex-row items-center justify-between py-2">
              <div className="gap-1">
                <p className="text-lg font-bold">{lead?.name}</p>

                <Button
                  variant="link"
                  onClick={handleDelete}
                  disabled={deleteLeadMutation.isPending}
                  className="justify-start px-0 text-error"
                >
                  {deleteLeadMutation.isPending ? "Excluindo" : "Excluir"}
                </Button>
              </div>

              <Button
                className={`flex items-center justify-center gap-2 rounded-full bg-[#199E4B] shadow-[0_0_10px_rgba(0,0,0,0.1)] hover:opacity-90 ${
                  isContacted ? "opacity-50" : ""
                }`}
                disabled={isContacted}
                onClick={handleContact}
              >
                <p className="font-bold text-white">Contatar</p>
                <img src="/whatsapp.svg" alt="icone whatsapp" />
              </Button>
            </div>

            <div className="h-[1px] w-full bg-gray02" />

            <div className="max-h-[400px] self-stretch overflow-y-auto py-4 pr-2">
              <div className="gap-4">
                {lead?.history.length === 0 ? (
                  <p className="text-gray01">Não possui atualizações</p>
                ) : (
                  lead?.history.map((group) => (
                    <div key={group.date}>
                      <p className="font-bold text-primary">
                        {formatDayLabel(group.date)}
                      </p>

                      {group.items.map((item, index) => (
                        <div
                          key={index}
                          className="mb-4 mt-1 flex flex-row items-center gap-2"
                        >
                          <Clock size={16} color="#ABABAB" />

                          <p className="text-sm text-black04">
                            {formatTime(item.updatedAt)}
                          </p>

                          <div className="ml-5 flex flex-col">
                            <p className="text-sm text-black04">
                              {item.action}
                            </p>

                            {item.value != null && (
                              <div className="border-figma-gray-200 mt-2 w-fit rounded-full border px-2 py-1">
                                <p className="text-sm">
                                  {masks.currency(item.value * 100)}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="mt-6 flex w-full justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="w-[160px] rounded-full text-primary"
              >
                Voltar
              </Button>

              <Button
                className="w-[160px] rounded-full font-semibold text-white"
                onClick={handleSimulation}
              >
                Realizar proposta
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DetailsLeadModal;
